import React, { FC, useMemo } from 'react';
import {
    Autocomplete,
    Box,
    Stepper,
    Step,
    StepLabel,
    TextField,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    FormControlLabel,
    Checkbox,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Typography,
    InputAdornment,
    Alert,
    Tooltip,
    Button,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';
import { Controller, useWatch } from 'react-hook-form';
import type { SchedulingRuleFormValues } from 'schemas/SchedulingRule';
import type { Gravity } from 'repository/schedule/@types/SchedulingRuleModel';
import type { ShiftProfileGroup } from 'repository/schedule/@types/ShiftProfileGroup';

const WEEKDAY_VALUES = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
const GRANULARITY_VALUES = ['15', '30', '60'] as const;
const SR = 'scheduling_rules.scheduling_rule';
const MINUTES_PER_DAY = 24 * 60;
const DURATION_TIME_REGEX = /^((?:[01]\d|2[0-3]):[0-5]\d|24:00)$/;

const parseTimeStringToMinutes = (value?: string): number | null => {
    if (!value) return null;
    const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
    if (!match) return null;
    const hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    return hours * 60 + minutes;
};

const formatMinutesToHHMM = (minutes: number): string => {
    const normalized = Math.max(0, Math.min(MINUTES_PER_DAY, Math.trunc(minutes)));
    if (normalized === MINUTES_PER_DAY) {
        return '24:00';
    }

    const hours = Math.trunc(normalized / 60)
        .toString()
        .padStart(2, '0');
    const mins = (normalized % 60).toString().padStart(2, '0');
    return `${hours}:${mins}`;
};

const parseDurationToMinutes = (value?: string): number | null => {
    if (!value || !DURATION_TIME_REGEX.test(value)) return null;
    if (value === '24:00') return MINUTES_PER_DAY;
    return parseTimeStringToMinutes(value);
};

const getNightWindowDurationMinutes = (startTime: string, endTime: string): number => {
    const startMinutes = parseTimeStringToMinutes(startTime);
    const endMinutes = parseTimeStringToMinutes(endTime);
    if (startMinutes === null || endMinutes === null) {
        return 0;
    }
    if (startMinutes === endMinutes) {
        return MINUTES_PER_DAY;
    }
    return (endMinutes - startMinutes + MINUTES_PER_DAY) % MINUTES_PER_DAY;
};

const clampDurationToNightWindow = (value: string, startTime: string, endTime: string): string => {
    const valueMinutes = parseDurationToMinutes(value);
    if (valueMinutes === null) return '00:00';
    const maxDuration = getNightWindowDurationMinutes(startTime, endTime);
    return formatMinutesToHHMM(Math.min(valueMinutes, maxDuration));
};

type SchedulingRuleFormMode = 'add' | 'edit';

interface StepNumberIconProps {
    icon: number;
    active: boolean;
    completed: boolean;
}

const StepNumberIcon: FC<StepNumberIconProps> = ({ icon, active, completed }) => (
    <Box
        sx={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: active ? 'primary.main' : completed ? 'primary.light' : 'grey.400',
            color: 'white',
            fontWeight: 700,
            fontSize: 14,
            transition: 'background-color 0.2s',
        }}
    >
        {icon}
    </Box>
);

interface GravityRowProps {
    label: string;
    fieldPrefix: keyof SchedulingRuleFormValues;
    control: any;
    errors: any;
    valueType?: 'decimal' | 'integer';
    tooltip?: string;
}

const GravityRow: FC<GravityRowProps> = ({ label, fieldPrefix, control, errors, valueType = 'decimal', tooltip }) => {
    const { t } = useTranslation();
    const granularity = useWatch({ control, name: 'granularity' }) as string;
    const currentGranularity = GRANULARITY_VALUES.includes(granularity as (typeof GRANULARITY_VALUES)[number]) ? granularity : '15';
    const gravityError = errors?.[fieldPrefix]?.gravity?.message;
    const valueError = errors?.[fieldPrefix]?.value?.message;
    const isPeriodWorkHoursField = fieldPrefix === 'minPeriodWorkHours' || fieldPrefix === 'maxPeriodWorkHours';

    const valueTypeTooltip =
        tooltip ??
        (valueType === 'decimal'
            ? t(
                  `${SR}.dialog.tooltips.gravity_value_decimal`,
                  `Este campo acepta hasta 2 decimales en formato H.MM y debe respetar la granularidad (${currentGranularity}): 15 => 2.15, 5.30; 30 => 10.30; 60 => solo enteros.`,
              )
            : t(`${SR}.dialog.tooltips.gravity_value_integer`, 'Este campo solo acepta numeros enteros (ej: 0, 1, 99).'));

    const maxValue = valueType === 'decimal' ? (isPeriodWorkHoursField ? 168 : 24) : 99;

    const gravityOptions = useMemo(
        () => [
            { value: 'not_considered' as Gravity, label: t(`${SR}.dialog.gravity.not_considered`) },
            { value: 'no_safeguard' as Gravity, label: t(`${SR}.dialog.gravity.no_safeguard`) },
            { value: 'warning' as Gravity, label: t(`${SR}.dialog.gravity.warning`) },
        ],
        [t],
    );

    return (
        <TableRow>
            <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body2">{label}</Typography>
            </TableCell>
            <TableCell sx={{ borderRight: '1px solid', borderColor: 'divider', minWidth: 160 }}>
                <Controller
                    name={`${fieldPrefix}.gravity` as any}
                    control={control}
                    render={({ field }) => (
                        <FormControl size="small" fullWidth error={Boolean(gravityError)}>
                            <Select {...field}>
                                {gravityOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </TableCell>
            <TableCell sx={{ minWidth: 140 }}>
                <Controller
                    name={`${fieldPrefix}.value` as any}
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            value={field.value ?? ''}
                            onChange={(event) => {
                                const rawValue = event.target.value;
                                if (rawValue === '') {
                                    field.onChange(0);
                                    return;
                                }

                                const normalizedRawValue = rawValue.replace(',', '.');
                                const parsedValue = valueType === 'decimal' ? Number.parseFloat(normalizedRawValue) : Number.parseInt(normalizedRawValue, 10);
                                if (Number.isNaN(parsedValue)) {
                                    field.onChange(0);
                                    return;
                                }

                                const boundedValue = Math.min(maxValue, Math.max(0, parsedValue));
                                const nextValue = valueType === 'decimal' ? Math.round(boundedValue * 100) / 100 : Math.trunc(boundedValue);
                                field.onChange(nextValue);
                            }}
                            size="small"
                            fullWidth
                            type="number"
                            error={Boolean(valueError)}
                            helperText={valueError}
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <Tooltip title={valueTypeTooltip}>
                                                <InfoOutlinedIcon fontSize="small" color="action" />
                                            </Tooltip>
                                        </InputAdornment>
                                    ),
                                },
                                htmlInput: {
                                    step: valueType === 'decimal' ? (currentGranularity === '60' ? '1' : '0.01') : '1',
                                    min: 0,
                                    max: maxValue,
                                    inputMode: valueType === 'decimal' ? 'decimal' : 'numeric',
                                    pattern: valueType === 'decimal' ? '^([0-9]{1,3})(\\.[0-9]{0,2})?$' : '[0-9]*',
                                },
                            }}
                        />
                    )}
                />
            </TableCell>
        </TableRow>
    );
};

interface GravityTableProps {
    title: string;
    col1Header?: string;
    col3Header?: string;
    rows: { label: string; field: keyof SchedulingRuleFormValues; valueType?: 'decimal' | 'integer'; tooltip?: string }[];
    control: any;
    errors: any;
}

const GravityTable: FC<GravityTableProps> = ({ title, col1Header, col3Header, rows, control, errors }) => {
    const { t } = useTranslation();
    return (
        <Box mb={3}>
            <Typography variant="subtitle1" fontWeight={600} mb={1}>
                {title}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.50' }}>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid', borderColor: 'divider' }}>{col1Header ?? t(`${SR}.dialog.table_headers.parameters`)}</TableCell>
                            <TableCell sx={{ fontWeight: 600, borderRight: '1px solid', borderColor: 'divider' }}>{t(`${SR}.dialog.table_headers.gravity`)}</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>{col3Header ?? t(`${SR}.dialog.table_headers.value`)}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <GravityRow
                                key={row.field as string}
                                label={row.label}
                                fieldPrefix={row.field}
                                control={control}
                                errors={errors}
                                valueType={row.valueType ?? 'integer'}
                                tooltip={row.tooltip}
                            />
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

const StepGeneral: FC<{ control: any; errors: any; mode: SchedulingRuleFormMode; shiftProfileGroups: ShiftProfileGroup[]; loadingShiftProfileGroups: boolean }> = ({
    control,
    errors,
    shiftProfileGroups,
    loadingShiftProfileGroups,
}) => {
    const { t } = useTranslation();
    const nightStartTime = (useWatch({ control, name: 'nightStartTime' }) as string) || '00:00';
    const nightEndTime = (useWatch({ control, name: 'nightEndTime' }) as string) || '00:00';
    const maxNightLimitTime = useMemo(() => formatMinutesToHHMM(getNightWindowDurationMinutes(nightStartTime, nightEndTime)), [nightStartTime, nightEndTime]);

    const weekdays = useMemo(() => WEEKDAY_VALUES.map((v) => ({ value: v, label: t(`general.days_week.${v}`) })), [t]);
    const granularityOptions = useMemo(() => GRANULARITY_VALUES.map((v) => ({ value: v, label: t(`${SR}.dialog.granularity_options.${v}`) })), [t]);
    const shiftProfileGroupOptions = useMemo(
        () => [
            { value: null as number | null, label: t('general.none', 'Ninguna') },
            ...shiftProfileGroups.map((group) => ({ value: group.shift_profile_group_id, label: group.shift_profile_group_name })),
        ],
        [shiftProfileGroups, t],
    );

    return (
        <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t(`${SR}.dialog.sections.general_config`)}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="name"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t(`${SR}.dialog.inputs.name`)}
                            placeholder={t(`${SR}.dialog.inputs.name_placeholder`)}
                            fullWidth
                            autoComplete="off"
                            error={Boolean(errors.name)}
                            helperText={errors.name?.message}
                            slotProps={{ htmlInput: { maxLength: 100 } }}
                            required
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="weekStartDay"
                    control={control}
                    render={({ field }) => (
                        <FormControl fullWidth required>
                            <InputLabel id="week-start-day-label">{t(`${SR}.dialog.inputs.week_start_day`)}</InputLabel>
                            <Select labelId="week-start-day-label" {...field} label={t(`${SR}.dialog.inputs.week_start_day`)}>
                                {weekdays.map((d) => (
                                    <MenuItem key={d.value} value={d.value}>
                                        {d.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
                <Controller
                    name="shiftProfileGroupId"
                    control={control}
                    render={({ field }) => (
                        <Autocomplete
                            options={shiftProfileGroupOptions}
                            getOptionLabel={(option) => option.label}
                            value={shiftProfileGroupOptions.find((option) => option.value === (field.value ?? null)) ?? shiftProfileGroupOptions[0]}
                            onChange={(_, selectedOption) => {
                                field.onChange(selectedOption?.value ?? null);
                            }}
                            isOptionEqualToValue={(option, value) => option.value === value.value}
                            loading={loadingShiftProfileGroups}
                            disabled={loadingShiftProfileGroups}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={t(`${SR}.dialog.inputs.shift_profile_group`)}
                                    error={Boolean(errors.shiftProfileGroupId)}
                                    helperText={errors.shiftProfileGroupId?.message}
                                />
                            )}
                        />
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Controller
                    name="description"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            label={t(`${SR}.dialog.inputs.description`)}
                            placeholder={t(`${SR}.dialog.inputs.description_placeholder`)}
                            fullWidth
                            multiline
                            rows={2}
                            autoComplete="off"
                            error={Boolean(errors.description)}
                            helperText={errors.description?.message}
                            slotProps={{ htmlInput: { maxLength: 255 } }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12 }} mt={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t(`${SR}.dialog.sections.night_definition`)}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="nightStartTime"
                    control={control}
                    render={({ field }) => <TextField {...field} label={t(`${SR}.dialog.inputs.night_start_time`)} fullWidth type="time" slotProps={{ inputLabel: { shrink: true } }} />}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="nightEndTime"
                    control={control}
                    render={({ field }) => <TextField {...field} label={t(`${SR}.dialog.inputs.night_end_time`)} fullWidth type="time" slotProps={{ inputLabel: { shrink: true } }} />}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="nightLimitTime"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            value={field.value ?? '00:00'}
                            onChange={(event) => {
                                field.onChange(event.target.value);
                            }}
                            onBlur={(event) => {
                                const rawValue = event.target.value;
                                field.onChange(clampDurationToNightWindow(rawValue, nightStartTime, nightEndTime));
                                field.onBlur();
                            }}
                            label={t(`${SR}.dialog.inputs.night_limit_time`)}
                            fullWidth
                            type="text"
                            placeholder="HH:MM"
                            error={Boolean(errors.nightLimitTime)}
                            helperText={errors.nightLimitTime?.message}
                            slotProps={{
                                inputLabel: { shrink: true },
                                htmlInput: {
                                    inputMode: 'numeric',
                                    pattern: '^((?:[01]\\d|2[0-3]):[0-5]\\d|24:00)$',
                                    maxLength: 5,
                                    'aria-label': `${t(`${SR}.dialog.inputs.night_limit_time`)} (00:00 - ${maxNightLimitTime})`,
                                },
                            }}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12 }} mt={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t(`${SR}.dialog.sections.segments`)}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="maxSegmentsPerShift"
                    control={control}
                    render={({ field }) => (
                        <TextField
                            {...field}
                            onChange={(event) => field.onChange(parseInt(event.target.value, 10) || 0)}
                            label={t(`${SR}.dialog.inputs.max_segments_per_shift`)}
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, max: 99 }}
                            error={Boolean(errors.maxSegmentsPerShift)}
                            helperText={errors.maxSegmentsPerShift?.message}
                        />
                    )}
                />
            </Grid>

            <Grid size={{ xs: 12 }} mt={1}>
                <Typography variant="subtitle1" fontWeight={600}>
                    {t(`${SR}.dialog.sections.others`)}
                </Typography>
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="granularity"
                    control={control}
                    render={({ field }) => (
                        <FormControl fullWidth>
                            <InputLabel id="granularity-label">{t(`${SR}.dialog.inputs.granularity`)}</InputLabel>
                            <Select {...field} labelId="granularity-label" label={t(`${SR}.dialog.inputs.granularity`)}>
                                {granularityOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
                <Controller
                    name="borderGranularity"
                    control={control}
                    render={({ field }) => (
                        <FormControl fullWidth>
                            <InputLabel id="border-granularity-label">{t(`${SR}.dialog.inputs.border_granularity`)}</InputLabel>
                            <Select {...field} labelId="border-granularity-label" label={t(`${SR}.dialog.inputs.border_granularity`)}>
                                {granularityOptions.map((opt) => (
                                    <MenuItem key={opt.value} value={opt.value}>
                                        {opt.label}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                />
            </Grid>
            <Grid size={{ xs: 12 }}>
                <Controller
                    name="considerPreviousPeriod"
                    control={control}
                    render={({ field }) => <FormControlLabel control={<Checkbox {...field} checked={field.value} />} label={t(`${SR}.dialog.inputs.consider_previous_period`)} />}
                />
            </Grid>
        </Grid>
    );
};

const StepHoras: FC<{ control: any; errors: any }> = ({ control, errors }) => {
    const { t } = useTranslation();
    return (
        <GravityTable
            title=""
            col1Header={t(`${SR}.dialog.table_headers.parameters`)}
            col3Header={t(`${SR}.dialog.table_headers.value`)}
            rows={[
                { label: t(`${SR}.dialog.hours_params.min_daily_work_hours`), field: 'minDailyWorkHours', valueType: 'decimal' },
                { label: t(`${SR}.dialog.hours_params.max_daily_work_hours`), field: 'maxDailyWorkHours', valueType: 'decimal' },
                { label: t(`${SR}.dialog.hours_params.min_period_work_hours`), field: 'minPeriodWorkHours', valueType: 'decimal' },
                { label: t(`${SR}.dialog.hours_params.max_period_work_hours`), field: 'maxPeriodWorkHours', valueType: 'decimal' },
                { label: t(`${SR}.dialog.hours_params.min_hours_between_shifts`), field: 'minHoursBetweenShifts', valueType: 'decimal' },
            ]}
            control={control}
            errors={errors}
        />
    );
};

const StepDias: FC<{ control: any; errors: any }> = ({ control, errors }) => {
    const { t } = useTranslation();
    return (
        <Box>
            <GravityTable
                title={t(`${SR}.dialog.sections.work_days_period`)}
                col1Header={t(`${SR}.dialog.table_headers.rule`)}
                col3Header={t(`${SR}.dialog.table_headers.quantity`)}
                rows={[
                    { label: t(`${SR}.dialog.days_params.min_work_days`), field: 'minWorkDays', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.max_work_days`), field: 'maxWorkDays', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.min_consecutive_work_days`), field: 'minConsecutiveWorkDays', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.max_consecutive_work_days`), field: 'maxConsecutiveWorkDays', valueType: 'integer' },
                ]}
                control={control}
                errors={errors}
            />
            <GravityTable
                title={t(`${SR}.dialog.sections.rest_duration_period`)}
                col1Header={t(`${SR}.dialog.table_headers.rule`)}
                col3Header={t(`${SR}.dialog.table_headers.quantity`)}
                rows={[
                    { label: t(`${SR}.dialog.days_params.min_consecutive_days_off`), field: 'minConsecutiveDaysOff', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.max_consecutive_days_off`), field: 'maxConsecutiveDaysOff', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.min_days_between_night_shifts`), field: 'minDaysBetweenNightShifts', valueType: 'integer' },
                    { label: t(`${SR}.dialog.days_params.min_days_between_rest_days`), field: 'minDaysBetweenRestDays', valueType: 'integer' },
                ]}
                control={control}
                errors={errors}
            />
        </Box>
    );
};

const StepNoches: FC<{ control: any; errors: any }> = ({ control, errors }) => {
    const { t } = useTranslation();
    return (
        <GravityTable
            title={t(`${SR}.dialog.sections.night_shifts_period`)}
            col1Header={t(`${SR}.dialog.table_headers.rule`)}
            col3Header={t(`${SR}.dialog.table_headers.quantity`)}
            rows={[
                { label: t(`${SR}.dialog.nights_params.min_night_shifts`), field: 'minNightShifts', valueType: 'integer' },
                { label: t(`${SR}.dialog.nights_params.max_night_shifts`), field: 'maxNightShifts', valueType: 'integer' },
                { label: t(`${SR}.dialog.nights_params.min_consecutive_night_shifts`), field: 'minConsecutiveNightShifts', valueType: 'integer' },
                { label: t(`${SR}.dialog.nights_params.max_consecutive_night_shifts`), field: 'maxConsecutiveNightShifts', valueType: 'integer' },
                { label: t(`${SR}.dialog.nights_params.min_days_between_groups`), field: 'minDaysBetweenGroups', valueType: 'integer' },
            ]}
            control={control}
            errors={errors}
        />
    );
};

interface SchedulingRuleFormProps {
    mode: SchedulingRuleFormMode;
    control: any;
    errors: any;
    shiftProfileGroups: ShiftProfileGroup[];
    loadingShiftProfileGroups: boolean;
    activeStep: number;
    setActiveStep: (value: number) => void;
    incompleteWarning: boolean;
    setIncompleteWarning: (value: boolean) => void;
}

export const SchedulingRuleForm: FC<SchedulingRuleFormProps> = ({
    mode,
    control,
    errors,
    shiftProfileGroups,
    loadingShiftProfileGroups,
    activeStep,
    setActiveStep,
    incompleteWarning,
    setIncompleteWarning,
}) => {
    const { t } = useTranslation();

    const stepLabels = useMemo(() => [t(`${SR}.dialog.steps.general`), t(`${SR}.dialog.steps.hours`), t(`${SR}.dialog.steps.days`), t(`${SR}.dialog.steps.nights`)], [t]);

    const isLastStep = activeStep === stepLabels.length - 1;

    const handleNext = () => {
        if (activeStep < stepLabels.length - 1) {
            setActiveStep(activeStep + 1);
        }
    };

    const handleBack = () => {
        if (activeStep > 0) {
            setActiveStep(activeStep - 1);
        }
    };

    return (
        <>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3, width: '100%' }}>
                {stepLabels.map((label, index) => (
                    <Step key={label}>
                        <StepLabel
                            slots={{
                                stepIcon: ({ active, completed }) => <StepNumberIcon icon={index + 1} active={active ?? false} completed={completed ?? false} />,
                            }}
                        >
                            {label}
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>

            <Box sx={{ minHeight: 300 }}>
                {activeStep === 0 && <StepGeneral control={control} errors={errors} mode={mode} shiftProfileGroups={shiftProfileGroups} loadingShiftProfileGroups={loadingShiftProfileGroups} />}
                {activeStep === 1 && <StepHoras control={control} errors={errors} />}
                {activeStep === 2 && <StepDias control={control} errors={errors} />}
                {activeStep === 3 && <StepNoches control={control} errors={errors} />}
            </Box>

            {isLastStep && incompleteWarning && (
                <Alert severity="warning" onClose={() => setIncompleteWarning(false)} sx={{ mt: 2 }} icon={<>⚠️</>}>
                    <Typography fontWeight={600}>{t(`${SR}.dialog.incomplete_warning.title`)}</Typography>
                    <Typography variant="body2">{t(`${SR}.dialog.incomplete_warning.message`)}</Typography>
                </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                {activeStep > 0 ? (
                    <Button variant="contained" startIcon={<NavigateBeforeIcon />} onClick={handleBack}>
                        {t(`${SR}.dialog.navigation.back`)}
                    </Button>
                ) : (
                    <Box />
                )}
                {!isLastStep && (
                    <Button variant="contained" endIcon={<NavigateNextIcon />} onClick={handleNext}>
                        {t(`${SR}.dialog.navigation.next`)}
                    </Button>
                )}
            </Box>
        </>
    );
};
